import { memo, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { parseDateRangeFilterValue } from '@/components/data-table/filters-v2';
import { cn } from '@/lib/utils';

import { PLANNER_CONTAINER_ID, PLANNER_DATE_RANGE_FILTER_KEY } from '../../constants';
import { PLANNER_SIDEBAR_WIDTH_PX, REM_TO_PX } from '../../constants/sizes';
import { calculateCellWidth } from '../../helpers';
import { scrollPlannerToFilterRange } from '../../helpers/scroll/scroll-to-filter-range';
import usePlannerStore from '../../store';
import { EdgeSide } from '../../types/edge-side';

/** Vertical position of the arrows: at the filter range bar's level (bottom of the header) */
const INDICATOR_TOP_REM = 4.875;

interface EdgeArrowProps {
  side: EdgeSide;
  visible: boolean;
  label: string;
  onClick: () => void;
}

function EdgeArrow({ side, visible, label, onClick }: EdgeArrowProps) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute z-20 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md',
        // Zoom-in / zoom-out show-hide animation
        'transition-all duration-200 ease-out',
        visible ? 'scale-100 opacity-100' : 'pointer-events-none scale-0 opacity-0'
      )}
      style={{
        top: `${INDICATOR_TOP_REM}rem`,
        ...(side === EdgeSide.Left
          ? { left: `${PLANNER_SIDEBAR_WIDTH_PX + 12}px` }
          : { right: '12px' }),
      }}
    >
      {side === EdgeSide.Left ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </button>
  );
}

/**
 * Edge arrows pointing to the filter range bar when the user has scrolled it
 * out of view: the arrow appears on the side where the bar is (bar in the past
 * → left, bar in the future → right) at the bar's vertical level, and a click
 * navigates back to the range (animated when nearby, instant when far).
 */
export const FilterRangeEdgeIndicator = memo(() => {
  const { t } = useTranslation('authenticated.planner');
  const dates = usePlannerStore(store => store.dates);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const dateRangeValue = usePlannerStore(store => store.filters[PLANNER_DATE_RANGE_FILTER_KEY]);

  const rawValue = Array.isArray(dateRangeValue) ? dateRangeValue[0] : dateRangeValue;
  const parsed = parseDateRangeFilterValue(rawValue);
  const rangeStartMs = parsed?.startDate.getTime();
  const rangeEndMs = parsed?.endDate.getTime();

  const [offscreenSide, setOffscreenSide] = useState<EdgeSide | null>(null);

  useEffect(() => {
    const container = document.getElementById(PLANNER_CONTAINER_ID);
    if (!container || rangeStartMs == undefined || rangeEndMs == undefined || dates.length === 0) {
      setOffscreenSide(null);
      return;
    }

    const baseDate = dates[0].startOf('day');
    const cellWidthPx = calculateCellWidth(zoomLevel) * REM_TO_PX;
    const startIdx = dayjs(rangeStartMs).startOf('day').diff(baseDate, 'day');
    const endIdx = dayjs(rangeEndMs).startOf('day').diff(baseDate, 'day');

    let rafId: number | null = null;

    const compute = () => {
      // Bar bounds in scroll-content coordinates (day cells start after the sidebar)
      const barLeft = PLANNER_SIDEBAR_WIDTH_PX + startIdx * cellWidthPx;
      const barRight = PLANNER_SIDEBAR_WIDTH_PX + (endIdx + 1) * cellWidthPx;
      // Visible timeline area (the sticky sidebar covers the leftmost part)
      const viewLeft = container.scrollLeft + PLANNER_SIDEBAR_WIDTH_PX;
      const viewRight = container.scrollLeft + container.clientWidth;

      if (barRight < viewLeft) {
        setOffscreenSide(EdgeSide.Left);
      } else if (barLeft > viewRight) {
        setOffscreenSide(EdgeSide.Right);
      } else {
        setOffscreenSide(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        compute();
      });
    };

    compute();
    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [rangeStartMs, rangeEndMs, dates, zoomLevel]);

  if (!parsed) {
    return null;
  }

  const handleClick = () => {
    scrollPlannerToFilterRange(dayjs(parsed.startDate), dayjs(parsed.endDate));
  };

  const label = t('filters.dateRangeBar.scrollTo');

  return (
    <>
      <EdgeArrow
        side={EdgeSide.Left}
        visible={offscreenSide === EdgeSide.Left}
        label={label}
        onClick={handleClick}
      />
      <EdgeArrow
        side={EdgeSide.Right}
        visible={offscreenSide === EdgeSide.Right}
        label={label}
        onClick={handleClick}
      />
    </>
  );
});
